import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useBusinessContext } from '@/context/BusinessContext';

export interface OnboardingRow {
  id: string;
  business_id: string;
  onboarding_mode: 'guided' | 'self' | null;
  app_tour_completed: boolean;
  app_tour_step: number;
  checklist_dismissed: boolean;
  completed_steps: Record<string, boolean>;
  tab_tours_seen: Record<string, boolean>;
  wizard_summary: WizardSummary | null;
  onboarding_conversation_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface WizardSummary {
  businessName: string;
  teamCount: number;
  rolesCount: number;
  openingHoursSummary: string;
  enabledFeatures: Record<string, boolean>;
  hasQrCode: boolean;
  // Expanded fields for AI onboarding context
  address?: string;
  timezone?: string;
  setupMethod?: 'google' | 'manual';
  hasDotyposConnection?: boolean;
  countryCode?: string | null;
  openingHours?: Array<{ day: string; open: string | null; close: string | null; closed: boolean }>;
  roles?: Array<{ name: string; payRate: number | null; payType: string; hoursPerMonth: number | null }>;
  teamMembers?: Array<{ name: string; email: string; positionName: string }>;
}

export function useOnboardingSync() {
  const { selectedBusinessId } = useBusinessContext();
  const queryClient = useQueryClient();

  const queryKey = ['business-onboarding', selectedBusinessId];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!selectedBusinessId) return null;
      const { data, error } = await supabase
        .from('business_onboarding')
        .select('*')
        .eq('business_id', selectedBusinessId)
        .maybeSingle();

      if (error) {
        console.error('[useOnboardingSync] fetch error:', error);
        return null;
      }
      return data as OnboardingRow | null;
    },
    enabled: !!selectedBusinessId,
    staleTime: Infinity,
  });

  const upsertOnboarding = useCallback(
    async (partial: Partial<Omit<OnboardingRow, 'id' | 'business_id' | 'created_at'>>) => {
      if (!selectedBusinessId) return;

      const payload = {
        business_id: selectedBusinessId,
        ...partial,
        updated_at: new Date().toISOString(),
      };

      // Optimistic update
      queryClient.setQueryData(queryKey, (old: OnboardingRow | null | undefined) => {
        if (!old) return { ...payload, id: 'optimistic' } as OnboardingRow;
        return { ...old, ...payload };
      });

      const { error } = await supabase
        .from('business_onboarding')
        .upsert(payload, { onConflict: 'business_id' });

      if (error) {
        console.error('[useOnboardingSync] upsert error:', error);
        // Refetch on error to get accurate state
        queryClient.invalidateQueries({ queryKey });
      }
    },
    [selectedBusinessId, queryClient, queryKey]
  );

  const setOnboardingMode = useCallback(
    (mode: 'guided' | 'self') => upsertOnboarding({ onboarding_mode: mode }),
    [upsertOnboarding]
  );

  const markStepCompleted = useCallback(
    (key: string) => {
      const current = data?.completed_steps ?? {};
      if (current[key]) return; // Already completed
      upsertOnboarding({ completed_steps: { ...current, [key]: true } });
    },
    [data?.completed_steps, upsertOnboarding]
  );

  const markAppTourCompleted = useCallback(
    () => upsertOnboarding({ app_tour_completed: true }),
    [upsertOnboarding]
  );

  const markTabTourSeen = useCallback(
    (tabId: string) => {
      const current = data?.tab_tours_seen ?? {};
      if (current[tabId]) return;
      upsertOnboarding({ tab_tours_seen: { ...current, [tabId]: true } });
    },
    [data?.tab_tours_seen, upsertOnboarding]
  );

  const dismissChecklist = useCallback(
    () => upsertOnboarding({ checklist_dismissed: true }),
    [upsertOnboarding]
  );

  const setWizardSummary = useCallback(
    (summary: WizardSummary) => upsertOnboarding({ wizard_summary: summary }),
    [upsertOnboarding]
  );

  const setOnboardingConversationId = useCallback(
    (conversationId: string) => upsertOnboarding({ onboarding_conversation_id: conversationId }),
    [upsertOnboarding]
  );

  return {
    data,
    isLoading,
    upsertOnboarding,
    setOnboardingMode,
    markStepCompleted,
    markAppTourCompleted,
    markTabTourSeen,
    dismissChecklist,
    setWizardSummary,
    setOnboardingConversationId,
  };
}
